/**
 * LookupField — Combobox for FK (uuid) fields
 *
 * Uses Popover + Command (cmdk) to search entities by name/code via the
 * generic entity list endpoint:  GET /entities/:slug?search=xxx&limit=20&page=1
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { ChevronsUpDown, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

interface LookupOption {
  id: string;
  label: string;
  subLabel?: string;
}

interface LookupFieldProps {
  /** Entity slug to search against (e.g. 'business-parties') */
  entitySlug: string;
  /** Current UUID value */
  value: string;
  /** Callback with selected UUID */
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  triggerId?: string;
  name?: string;
  ariaLabelledBy?: string;
}

/** Label field heuristics — try name, then code, then first string field */
function pickLabel(record: Record<string, unknown>): string {
  const name = record.name ?? record.Name;
  const code = record.code ?? record.Code;
  if (name) return String(name);
  if (code) return String(code);
  // fallback: first non-id string value
  for (const [k, v] of Object.entries(record)) {
    if (k === 'id' || k === 'tenantId') continue;
    if (typeof v === 'string' && v.length > 0 && v.length < 200) return v;
  }
  return String(record.id ?? '');
}

function pickSubLabel(record: Record<string, unknown>): string | undefined {
  const name = record.name ?? record.Name;
  const code = record.code ?? record.Code;
  if (name && code) return String(code);
  return undefined;
}

export function LookupField({ entitySlug, value, onChange, placeholder, error, triggerId, name, ariaLabelledBy }: LookupFieldProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [displayLabel, setDisplayLabel] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Fetch display label for current value on mount
  useEffect(() => {
    if (!value) {
      setDisplayLabel('');
      return;
    }
    let cancelled = false;
    api
      .get<Record<string, unknown>>(`/entities/${entitySlug}/${value}`)
      .then((rec) => {
        if (!cancelled) setDisplayLabel(pickLabel(rec));
      })
      .catch(() => {
        if (!cancelled) setDisplayLabel(value.slice(0, 8) + '…');
      });
    return () => { cancelled = true; };
  }, [value, entitySlug]);

  // Search entities with debounce
  const doSearch = useCallback(
    (q: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const res = await api.get<{ items: Record<string, unknown>[] }>(
            `/entities/${entitySlug}?search=${encodeURIComponent(q)}&limit=20&page=1&includeTotal=false`,
          );
          setOptions(
            (res.items ?? []).map((r) => ({
              id: String(r.id),
              label: pickLabel(r),
              subLabel: pickSubLabel(r),
            })),
          );
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 250);
    },
    [entitySlug],
  );

  // Load initial options when popover opens
  useEffect(() => {
    if (open) doSearch(search);
  }, [open]);

  const handleSearch = (q: string) => {
    setSearch(q);
    doSearch(q);
  };

  const handleSelect = (id: string) => {
    onChange(id);
    const opt = options.find((o) => o.id === id);
    if (opt) setDisplayLabel(opt.label);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setDisplayLabel('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={triggerId}
          name={name}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-labelledby={ariaLabelledBy}
          aria-invalid={error ? 'true' : undefined}
          className={cn(
            'w-full justify-between font-normal h-9 text-sm',
            !value && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive',
          )}
        >
          <span className="truncate">{displayLabel || placeholder || 'Select…'}</span>
          <span className="flex items-center gap-0.5 shrink-0 ml-1">
            {value && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search…"
            value={search}
            onValueChange={handleSearch}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading…' : 'No results'}
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.id}
                  onSelect={() => handleSelect(opt.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === opt.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{opt.label}</span>
                    {opt.subLabel && (
                      <span className="text-xs text-muted-foreground">{opt.subLabel}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
